/**
 * Upload Page - File upload for QVT analysis
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { PageContent } from '../components/PageContent';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription } from '../components/ui/card';
import { X, Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

interface FileEntry {
  id: string;
  date: string;
  fileName: string;
}

export function UploadPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [files, setFiles] = useState<FileEntry[]>([{ id: '1', date: '', fileName: '' }]);

  const handleFileSelect = (id: string, file: File) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, fileName: file.name } : f)));
  };

  const handleDateChange = (id: string, date: string) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, date } : f)));
  };

  const handleRemoveFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleAddFile = () => {
    const newId = String(Date.now());
    setFiles((prev) => [...prev, { id: newId, date: '', fileName: '' }]);
  };

  const handleBeginAnalysis = () => {
    toast({
      variant: 'success',
      title: 'Upload successful',
      description: 'Analysis has begun.',
    });
    void navigate('/qvt-primer');
  };

  const hasFiles = files.some((f) => f.fileName);

  return (
    <Layout>
      <PageHeader
        title="Upload Patient Data"
        description="Upload de-identified CT scans for QVT analysis in the QVT Demo project"
      />
      <PageContent width="md">
        <Card>
          <CardContent className="p-6 space-y-6">
            <CardDescription className="space-y-2">
              <p>
                Please upload de-identified, pre-treatment standard dose diagnostic chest CT scans
                from patients with NSCLC. You can upload multiple time points for longitudinal
                analysis.
              </p>
              <p className="text-sm font-medium">Accepted types: zip, nii, gz, nrrd, mha, dcm</p>
            </CardDescription>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Files:</h3>
              <div className="space-y-3" id="fileList">
                {files.map((file) => (
                  <div key={file.id} className="flex gap-3 items-start" data-file-id={file.id}>
                    <div className="w-32">
                      <Label htmlFor={`date-${file.id}`} className="sr-only">
                        Date
                      </Label>
                      <Input
                        id={`date-${file.id}`}
                        type="text"
                        maxLength={10}
                        placeholder="10/31/2025"
                        value={file.date}
                        onChange={(e) => handleDateChange(file.id, e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type="file"
                        className="hidden"
                        accept=".zip,.nii,.gz,.nrrd,.mha,.dcm"
                        id={`file-input-${file.id}`}
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0];
                          if (selectedFile) {
                            handleFileSelect(file.id, selectedFile);
                          }
                        }}
                      />
                      <Label
                        htmlFor={`file-input-${file.id}`}
                        className="flex items-center justify-center h-10 px-4 py-2 border border-input bg-background rounded-md cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        {file.fileName || 'Drop file or click to browse'}
                      </Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveFile(file.id)}
                      disabled={files.length === 1}
                      className="delete-button"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={handleAddFile} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Another Time Point
              </Button>
            </div>
            <div className="flex justify-end pt-4">
              <Button
                id="accept-button"
                disabled={!hasFiles}
                onClick={handleBeginAnalysis}
                data-testid="begin-analysis-button"
              >
                Begin Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageContent>
    </Layout>
  );
}
